/**
 * Extract variable names from a template string.
 * Supports ${var} and ${var:filter} syntax.
 * @param {string} template
 * @returns {string[]}
 */
export function extractVariables(template) {
  if (!template) {
    return [];
  }
  const regex = /\${(.*?)}/g;
  const matches = template.matchAll(regex);
  return Array.from(matches, (m) => m[1].split(":")[0].trim());
}

/**
 * Resolve a template string by replacing ${var} placeholders with values.
 * Supports the :upper filter.
 * When a variable has no value, uses the field's default if available.
 * Applies prefix/suffix around the resolved value (only if non-empty).
 * @param {string} template
 * @param {Object} values - key/value map of field values
 * @param {Array} [fields] - optional array of field definitions (with default, prefix, suffix)
 * @returns {string}
 */
export function resolveTemplate(template, values, fields) {
  if (!template) {
    return "";
  }
  return template.replace(/\${(.*?)}/g, (match, key) => {
    const [fieldName, filter] = key.trim().split(":");
    let value = values[fieldName];

    const fieldDef = fields?.find((f) => f.name === fieldName);

    if (value === undefined || value === null || value === "") {
      if (
        fieldDef?.default !== undefined &&
        fieldDef?.default !== null &&
        fieldDef?.default !== ""
      ) {
        value = fieldDef.default;
      } else {
        return "";
      }
    }

    let processedValue = String(value);
    if (filter === "upper") {
      processedValue = processedValue.toUpperCase();
    }

    const prefix = fieldDef?.prefix || "";
    const suffix = fieldDef?.suffix || "";

    return prefix + processedValue + suffix;
  });
}

/**
 * Parse a filename to extract the base name, increment, and extension.
 * "rapport (3).pdf" → { baseName: "rapport", increment: 3, extension: ".pdf" }
 * "rapport.pdf" → { baseName: "rapport", increment: 0, extension: ".pdf" }
 * "rapport" → { baseName: "rapport", increment: 0, extension: "" }
 * @param {string} fileName
 * @returns {{ baseName: string, increment: number, extension: string }}
 */
export function parseFileName(fileName) {
  const dotIndex = fileName.lastIndexOf(".");
  const name = dotIndex > 0 ? fileName.substring(0, dotIndex) : fileName;
  const extension = dotIndex > 0 ? fileName.substring(dotIndex) : "";

  const match = name.match(/^(.*?)\s+\((\d+)\)$/);
  if (match) {
    return { baseName: match[1], increment: parseInt(match[2], 10), extension };
  }
  return { baseName: name, increment: 0, extension };
}

/**
 * Build an incremented filename.
 * ("rapport", 3, ".pdf") → "rapport (3).pdf"
 * ("rapport", 0, ".pdf") → "rapport.pdf"
 * @param {string} baseName
 * @param {number} increment
 * @param {string} extension
 * @returns {string}
 */
export function buildIncrementedName(baseName, increment, extension) {
  if (increment === 0) {
    return baseName + extension;
  }
  return `${baseName} (${increment})${extension}`;
}

/**
 * Deduplicate a list of filenames against each other and against existing names.
 * @param {string[]} newNames - the names to deduplicate
 * @param {string[]} existingNames - the names already present in the folder
 * @returns {string[]} - deduplicated names, in the same order
 */
export function deduplicateFileNames(newNames, existingNames) {
  // Build a map of baseName+extension → highest known increment
  const highestIncrement = new Map();
  // Track all known names (lowercase for case-insensitive matching)
  const knownNames = new Set();

  for (const name of existingNames) {
    knownNames.add(name);
    const { baseName, increment, extension } = parseFileName(name);
    const key = baseName + extension;
    const current = highestIncrement.get(key) || 0;
    if (increment > current) {
      highestIncrement.set(key, increment);
    }
  }

  const result = [];
  for (const name of newNames) {
    const { baseName, extension } = parseFileName(name);
    const key = baseName + extension;

    if (knownNames.has(name)) {
      // Name already exists → increment
      const current = highestIncrement.get(key) || 0;
      const next = current + 1;
      const newName = buildIncrementedName(baseName, next, extension);
      highestIncrement.set(key, next);
      knownNames.add(newName);
      result.push(newName);
    } else {
      // Name is unique → keep it, but register it
      knownNames.add(name);
      const { increment } = parseFileName(name);
      const current = highestIncrement.get(key) || 0;
      if (increment > current) {
        highestIncrement.set(key, increment);
      }
      result.push(name);
    }
  }

  return result;
}

/**
 * Build a regex from a nature's cm:name template string.
 * Each ${field} placeholder becomes a named capture group.
 * @param {string} template - e.g. "${NUMMARCHE}_${NATURE}"
 * @param {Map<string, Object>} fieldMap - Map of field name → field definition
 * @returns {{ regex: RegExp, fieldNames: Array<{groupName: string, fieldName: string}> } | null}
 */
export function buildTemplateRegex(template, fieldMap) {
  if (template == null || !fieldMap) {
    return null;
  }

  const tokens = [];
  let remaining = template;

  while (remaining.length > 0) {
    const varMatch = remaining.match(/^\$\{([^}]+)\}/);
    if (varMatch) {
      tokens.push({ type: "var", name: varMatch[1] });
      remaining = remaining.slice(varMatch[0].length);
    } else {
      const nextVar = remaining.indexOf("${");
      if (nextVar === -1) {
        tokens.push({ type: "literal", value: remaining });
        remaining = "";
      } else {
        tokens.push({ type: "literal", value: remaining.slice(0, nextVar) });
        remaining = remaining.slice(nextVar);
      }
    }
  }

  const fieldNames = [];
  let regexStr = "^";
  for (const token of tokens) {
    if (token.type === "literal") {
      regexStr += token.value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    } else {
      const field = fieldMap.get(token.name);
      const isOptional = field?.optional === true;
      const prefix = field?.prefix || "";
      const escapedPrefix = prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      // Sanitize field name for use as regex group name (only [a-zA-Z_][a-zA-Z0-9_]*)
      let groupName = token.name.replace(/[^a-zA-Z0-9_]/g, "_");
      if (/^[0-9]/.test(groupName)) {
        groupName = "_" + groupName;
      }
      fieldNames.push({ groupName, fieldName: token.name });

      if (isOptional) {
        regexStr += `(?:${escapedPrefix}(?<${groupName}>.+?))?`;
      } else {
        regexStr += `(?<${groupName}>.+?)`;
      }
    }
  }
  regexStr += "$";

  try {
    return { regex: new RegExp(regexStr), fieldNames };
  } catch {
    return null;
  }
}

/**
 * Compute the specificity score of a template string.
 * Specificity = length of static text (everything outside ${…}) after removing underscores.
 * @param {string} template - e.g. "${NUM}-FAC-${NATURE}"
 * @returns {number}
 */
export function computeTemplateSpecificity(template) {
  const staticText = template.replace(/\$\{[^}]+\}/g, "");
  return staticText.replace(/_/g, "").length;
}

/**
 * Detect a document nature by matching a filename against nature templates.
 * @param {string} filename - filename with extension
 * @param {Array} natures - array of nature definitions (with metadata["cm:name"].template)
 * @param {Array} fields - array of field definitions
 * @returns {{ nature: Object, extractedMetadata: Object } | null}
 */
export function detectNatureFromFilename(filename, natures, fields) {
  if (!filename || !natures || !fields) {
    return null;
  }

  const fieldMap = new Map();
  for (const field of fields) {
    fieldMap.set(field.name, field);
  }

  const nameWithoutExt = filename.replace(/\.[^.]+$/, "");
  let bestMatch = null;
  let bestSpecificity = 0;
  let bestExtractedMetadata = {};

  for (const nature of natures) {
    const template = nature.metadata?.["cm:name"]?.template;
    if (!template) {
      continue;
    }

    const result = buildTemplateRegex(template, fieldMap);
    if (!result) {
      continue;
    }

    const { regex, fieldNames } = result;
    const execResult = regex.exec(nameWithoutExt);
    if (execResult) {
      const specificity = computeTemplateSpecificity(template);
      if (specificity > bestSpecificity) {
        bestSpecificity = specificity;
        bestMatch = nature;
        // Extract metadata values from named groups
        const extracted = {};
        for (const { groupName, fieldName } of fieldNames) {
          const value = execResult.groups?.[groupName];
          if (value !== undefined) {
            extracted[fieldName] = value;
          }
        }
        bestExtractedMetadata = extracted;
      }
    }
  }

  if (!bestMatch) {
    return null;
  }
  return { nature: bestMatch, extractedMetadata: bestExtractedMetadata };
}
